import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Brain, 
  MessageSquare, 
  FileText, 
  BarChart3, 
  Send, 
  Loader2, 
  RefreshCw,
  Trash2,
  Download,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Clock,
  Euro,
  Building2,
  FolderKanban,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AIAssistantPage = () => {
  const [activeTab, setActiveTab] = useState('insights');
  
  // Insights state
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [tipoAnalisi, setTipoAnalisi] = useState('generale');
  
  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const chatEndRef = useRef(null);
  
  // Reports state
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [tipoReport, setTipoReport] = useState('settimanale');
  const [selectedReport, setSelectedReport] = useState(null);
  
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Load initial data
  useEffect(() => {
    loadReports();
  }, []);

  // === INSIGHTS ===
  const loadInsights = async () => {
    setInsightsLoading(true);
    try {
      const response = await axios.post(`${API}/ai/insights`, {
        tipo_analisi: tipoAnalisi
      }, getAuthHeaders());
      
      setInsights(response.data);
      toast.success('Insights generati con successo');
    } catch (error) {
      console.error('Errore caricamento insights:', error);
      toast.error('Errore nel caricamento degli insights');
    } finally {
      setInsightsLoading(false);
    }
  };

  // === CHAT ===
  const sendChatMessage = async () => {
    if (!chatInput.trim()) return;
    
    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatLoading(true);
    
    try {
      const response = await axios.post(`${API}/ai/chat`, {
        messaggio: userMessage,
        session_id: sessionId
      }, getAuthHeaders());
      
      if (!sessionId) {
        setSessionId(response.data.session_id);
      }
      
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.risposta 
      }]);
    } catch (error) {
      console.error('Errore chat:', error);
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Mi dispiace, si è verificato un errore. Riprova tra poco.' 
      }]);
      toast.error('Errore nella comunicazione con l\'AI');
    } finally {
      setChatLoading(false);
    }
  };

  const clearChat = () => {
    setChatMessages([]);
    setSessionId(null);
    toast.success('Conversazione cancellata');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendChatMessage();
    }
  };

  // === REPORTS ===
  const loadReports = async () => {
    setReportsLoading(true);
    try {
      const response = await axios.get(`${API}/ai/reports`, getAuthHeaders());
      setReports(response.data.reports || []);
    } catch (error) {
      console.error('Errore caricamento reports:', error);
    } finally {
      setReportsLoading(false);
    }
  };

  const generateReport = async () => {
    setGeneratingReport(true);
    try {
      const response = await axios.post(`${API}/ai/report`, {
        tipo_report: tipoReport
      }, getAuthHeaders());
      
      setSelectedReport(response.data);
      loadReports();
      toast.success('Report generato con successo');
    } catch (error) {
      console.error('Errore generazione report:', error);
      toast.error('Errore nella generazione del report');
    } finally {
      setGeneratingReport(false);
    }
  };

  const viewReport = async (reportId) => {
    try {
      const response = await axios.get(`${API}/ai/reports/${reportId}`, getAuthHeaders());
      setSelectedReport(response.data.report);
    } catch (error) {
      console.error('Errore caricamento report:', error);
      toast.error('Errore nel caricamento del report');
    }
  };

  // Render markdown-like text with formatting
  const renderFormattedText = (text) => {
    if (!text) return null;
    
    return text.split('\n').map((line, idx) => {
      // Headers with emoji
      if (line.match(/^[📊⚠️✅🎯💰📈🔴🟡🟢]/)) {
        return <p key={idx} className="font-bold text-lg mt-4 mb-2">{line}</p>;
      }
      // Numbered items
      if (line.match(/^\d+\./)) {
        return <p key={idx} className="ml-4 my-1">{line}</p>;
      }
      // Bullet points
      if (line.match(/^[•\-\*]/)) {
        return <p key={idx} className="ml-4 my-1">{line}</p>;
      }
      // Section headers
      if (line.match(/^[A-Z]{2,}/) || line.includes('═')) {
        return <p key={idx} className="font-bold mt-3 text-slate-700">{line}</p>;
      }
      // Empty lines
      if (!line.trim()) {
        return <br key={idx} />;
      }
      return <p key={idx} className="my-1">{line}</p>;
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg">
              <Brain className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-800">PaMa AI Assistant</h1>
              <p className="text-slate-500">Analisi intelligente di progetti, fatturazione e cantieri</p>
            </div>
          </div>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid grid-cols-3 w-full max-w-lg bg-white shadow-sm">
            <TabsTrigger value="insights" className="flex items-center gap-2" data-testid="tab-insights">
              <BarChart3 className="w-4 h-4" />
              Dashboard Insights
            </TabsTrigger>
            <TabsTrigger value="chat" className="flex items-center gap-2" data-testid="tab-chat">
              <MessageSquare className="w-4 h-4" />
              Chatbot
            </TabsTrigger>
            <TabsTrigger value="reports" className="flex items-center gap-2" data-testid="tab-reports">
              <FileText className="w-4 h-4" />
              Report AI
            </TabsTrigger>
          </TabsList>

          {/* === TAB INSIGHTS === */}
          <TabsContent value="insights" className="space-y-6">
            {/* Controls */}
            <Card className="border-0 shadow-lg">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <Select value={tipoAnalisi} onValueChange={setTipoAnalisi}>
                    <SelectTrigger className="w-64" data-testid="select-tipo-analisi">
                      <SelectValue placeholder="Tipo di analisi" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="generale">Analisi Generale</SelectItem>
                      <SelectItem value="progetti">Focus Progetti</SelectItem>
                      <SelectItem value="fatturazione">Focus Fatturazione SAL</SelectItem>
                      <SelectItem value="cantieri">Focus Cantieri</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button 
                    onClick={loadInsights} 
                    disabled={insightsLoading}
                    className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                    data-testid="btn-generate-insights"
                  >
                    {insightsLoading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analizzando...</>
                    ) : (
                      <><Sparkles className="w-4 h-4 mr-2" /> Genera Insights</>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Stats Cards */}
            {insights?.statistiche && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="border-0 shadow-md bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-100 text-sm">Progetti Attivi</p>
                        <p className="text-3xl font-bold">{insights.statistiche.progetti_attivi}</p>
                      </div>
                      <FolderKanban className="w-10 h-10 text-blue-200" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-0 shadow-md bg-gradient-to-br from-amber-500 to-orange-500 text-white">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-amber-100 text-sm">In Ritardo</p>
                        <p className="text-3xl font-bold">{insights.statistiche.progetti_ritardo}</p>
                      </div>
                      <AlertTriangle className="w-10 h-10 text-amber-200" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-0 shadow-md bg-gradient-to-br from-green-500 to-emerald-500 text-white">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-green-100 text-sm">Cantieri Attivi</p>
                        <p className="text-3xl font-bold">{insights.statistiche.cantieri_attivi}</p>
                      </div>
                      <Building2 className="w-10 h-10 text-green-200" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-0 shadow-md bg-gradient-to-br from-purple-500 to-violet-500 text-white">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-purple-100 text-sm">Da Fatturare</p>
                        <p className="text-2xl font-bold">€{(insights.statistiche.importo_da_fatturare || 0).toLocaleString('it-IT')}</p>
                      </div>
                      <Euro className="w-10 h-10 text-purple-200" />
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Insights Content */}
            {insights?.insights && (
              <Card className="border-0 shadow-lg">
                <CardHeader className="border-b bg-gradient-to-r from-slate-50 to-blue-50">
                  <CardTitle className="flex items-center gap-2">
                    <Brain className="w-5 h-5 text-purple-600" />
                    Analisi AI - {tipoAnalisi.charAt(0).toUpperCase() + tipoAnalisi.slice(1)}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="prose prose-slate max-w-none" data-testid="insights-content">
                    {renderFormattedText(insights.insights)}
                  </div>
                </CardContent>
              </Card>
            )}

            {!insights && !insightsLoading && (
              <Card className="border-0 shadow-lg">
                <CardContent className="p-12 text-center">
                  <Brain className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-600 mb-2">Nessuna analisi generata</h3>
                  <p className="text-slate-400 mb-4">Seleziona il tipo di analisi e clicca "Genera Insights" per iniziare</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* === TAB CHAT === */}
          <TabsContent value="chat" className="space-y-4">
            <Card className="border-0 shadow-lg h-[600px] flex flex-col">
              <CardHeader className="border-b bg-gradient-to-r from-slate-50 to-blue-50 py-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <MessageSquare className="w-5 h-5 text-blue-600" />
                    PaMa Assistant
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={clearChat} data-testid="btn-clear-chat">
                    <Trash2 className="w-4 h-4 mr-1" />
                    Nuova Chat
                  </Button>
                </div>
              </CardHeader>
              
              {/* Chat Messages */}
              <CardContent className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="chat-messages">
                {chatMessages.length === 0 && (
                  <div className="text-center py-12">
                    <Brain className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 mb-4">Ciao! Sono PaMa Assistant.</p>
                    <p className="text-slate-400 text-sm mb-6">Chiedimi informazioni su progetti, cantieri, fatturazione...</p>
                    <div className="flex flex-wrap justify-center gap-2">
                      {[
                        'Quanti progetti sono in ritardo?',
                        'Qual è il totale da fatturare?',
                        'Come stanno andando i cantieri?',
                        'Dammi un riepilogo generale'
                      ].map((suggestion, idx) => (
                        <Button
                          key={idx}
                          variant="outline"
                          size="sm"
                          onClick={() => setChatInput(suggestion)}
                          className="text-xs"
                        >
                          {suggestion}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
                
                {chatMessages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] p-3 rounded-lg ${
                        msg.role === 'user'
                          ? 'bg-blue-600 text-white rounded-br-sm'
                          : 'bg-slate-100 text-slate-800 rounded-bl-sm'
                      }`}
                    >
                      <div className="whitespace-pre-wrap text-sm">{msg.content}</div>
                    </div>
                  </div>
                ))}
                
                {chatLoading && (
                  <div className="flex justify-start">
                    <div className="bg-slate-100 p-3 rounded-lg rounded-bl-sm">
                      <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                    </div>
                  </div>
                )}
                
                <div ref={chatEndRef} />
              </CardContent>
              
              {/* Chat Input */}
              <div className="border-t p-4 bg-slate-50">
                <div className="flex gap-2">
                  <Input
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Scrivi un messaggio..."
                    className="flex-1"
                    disabled={chatLoading}
                    data-testid="chat-input"
                  />
                  <Button 
                    onClick={sendChatMessage} 
                    disabled={chatLoading || !chatInput.trim()}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="btn-send-message"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* === TAB REPORTS === */}
          <TabsContent value="reports" className="space-y-6">
            {/* Generate Report */}
            <Card className="border-0 shadow-lg">
              <CardHeader className="border-b bg-gradient-to-r from-slate-50 to-blue-50">
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-green-600" />
                  Genera Nuovo Report
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <Select value={tipoReport} onValueChange={setTipoReport}>
                    <SelectTrigger className="w-64" data-testid="select-tipo-report">
                      <SelectValue placeholder="Tipo report" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="settimanale">Report Settimanale</SelectItem>
                      <SelectItem value="mensile">Report Mensile</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button 
                    onClick={generateReport}
                    disabled={generatingReport}
                    className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                    data-testid="btn-generate-report"
                  >
                    {generatingReport ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generando...</>
                    ) : (
                      <><FileText className="w-4 h-4 mr-2" /> Genera Report</>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Reports List */}
              <Card className="border-0 shadow-lg lg:col-span-1">
                <CardHeader className="border-b">
                  <CardTitle className="text-lg flex items-center justify-between">
                    Report Salvati
                    <Button variant="ghost" size="sm" onClick={loadReports}>
                      <RefreshCw className={`w-4 h-4 ${reportsLoading ? 'animate-spin' : ''}`} />
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-2 max-h-[500px] overflow-y-auto">
                  {reports.length === 0 ? (
                    <div className="text-center py-8 text-slate-400">
                      <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p>Nessun report generato</p>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {reports.map((report) => (
                        <div
                          key={report.id}
                          onClick={() => viewReport(report.id)}
                          className={`p-3 rounded-lg cursor-pointer transition-colors ${
                            selectedReport?.id === report.id || selectedReport?.report_id === report.id
                              ? 'bg-blue-100 border-l-4 border-blue-600'
                              : 'hover:bg-slate-50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-sm capitalize">{report.tipo}</span>
                            <span className="text-xs text-slate-400">
                              {new Date(report.created_at).toLocaleDateString('it-IT')}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">
                            Generato da {report.generato_da_nome || 'Sistema'}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Report Content */}
              <Card className="border-0 shadow-lg lg:col-span-2">
                <CardHeader className="border-b bg-gradient-to-r from-slate-50 to-green-50">
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                    {selectedReport ? `Report ${selectedReport.tipo_report || selectedReport.tipo}` : 'Seleziona un report'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 max-h-[600px] overflow-y-auto">
                  {selectedReport ? (
                    <div className="space-y-4">
                      {/* Stats Summary */}
                      {selectedReport.statistiche && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4 p-3 bg-slate-50 rounded-lg">
                          <div className="text-center">
                            <p className="text-2xl font-bold text-blue-600">{selectedReport.statistiche.progetti_attivi}</p>
                            <p className="text-xs text-slate-500">Progetti Attivi</p>
                          </div>
                          <div className="text-center">
                            <p className="text-2xl font-bold text-amber-600">{selectedReport.statistiche.progetti_ritardo}</p>
                            <p className="text-xs text-slate-500">In Ritardo</p>
                          </div>
                          <div className="text-center">
                            <p className="text-2xl font-bold text-green-600">{selectedReport.statistiche.cantieri_attivi}</p>
                            <p className="text-xs text-slate-500">Cantieri</p>
                          </div>
                          <div className="text-center">
                            <p className="text-lg font-bold text-purple-600">€{(selectedReport.statistiche.importo_da_fatturare || 0).toLocaleString('it-IT')}</p>
                            <p className="text-xs text-slate-500">Da Fatturare</p>
                          </div>
                        </div>
                      )}
                      
                      {/* Report Content */}
                      <div className="prose prose-slate max-w-none" data-testid="report-content">
                        {renderFormattedText(selectedReport.report || selectedReport.contenuto)}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-slate-400">
                      <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" />
                      <p>Seleziona un report dalla lista o generane uno nuovo</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AIAssistantPage;
